import { FiSnackBarService } from '@fi-app/shared/fi-snackbar/snack-bar.service';
import { Injectable, Injector, NgZone } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { ILightWeightObject, ILightWeightObjectCollection } from '@fi-shared/models';
import { PublicationType } from '@fi-shared/types/pubication-type';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { ChecklistTimeSettings } from './types/time-settings';
import { FiDialogConfirmDeleteComponent } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete.component';
import { IDialogConfirmDeleteData } from '@fi-shared/fi-dialogs-generic/confirm-delete/dialog-confirm-delete-data';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { ChecklistModel } from './checklist.model';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { CheckpointModel } from './checkpoint.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiChecklistService } from '@fi-shared/fi-shared-services/fi-checklist.service';
import { IFiChecklistModelBase, IFiChecklistViewerModel } from '@fi-shared/checklist';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { BehaviorSubject } from 'rxjs';
import { ISystemNotificationsCheckpointAnswer } from '@fi-shared/checklist/checklist';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IItemsListAction } from '@fi-app/components/items-list/items-list';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { createMap } from '@fi-shared/utils/create-map';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { fiTextToHtml } from '@fi-shared/utils/fi-text-to-html';

@Injectable()
export class ChecklistService extends FiChecklistService {
    timeSettings: [{ id: ChecklistTimeSettings; textId: string }, { id: ChecklistTimeSettings; textId: string }];
    timeSettingsMap: {
        [key: string]: { id: ChecklistTimeSettings; textId: string } | { id: ChecklistTimeSettings; textId: string };
    };

    constructor(
        fiBackend: FiBackendService,
        private fiDialogsService: FiDialogsService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        fiLoadingService: LoadingService,
        appTextService: AppTextService,
        private detectDevices: DetectDevicesService,
        fiSignalRService: FiSignalRService,
        private fiSignalRHelperService: FiSignalRHelperService,
        private contentLanguagesService: ContentLanguagesService,
        private stateService: FiStateService,
        private ngZone: NgZone,
        private snackBar: FiSnackBarService,
        private injector: Injector,
        private customerDataService: FiCustomerDataService,
        private fiFormatDateService: FiFormatDateService,
    ) {
        super(fiBackend, appTextService, fiLoadingService, fiSignalRService);
        this.timeSettings = [
            {
                id: ChecklistModel.TimeSettings.Recurring,
                textId: 'checklist.time_settings_recurring',
            },
            {
                id: ChecklistModel.TimeSettings.OneTime,
                textId: 'checklist.time_settings_one_time',
            },
        ];
        this.timeSettingsMap = createMap(this.timeSettings, 'id');
    }

    // note: not used ?
    getChecklistModel() {
        return ChecklistModel;
    }

    getChecklistInstance(checklist) {
        return new ChecklistModel(checklist, this.injector);
    }

    getCheckpointInstance(checkpoint?) {
        return new CheckpointModel(checkpoint, this.injector);
    }

    getCheckListsListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<any>>(
            'CheckList/CheckList/GetCheckListsListData',
            parameter,
        );
    }

    getCheckListsCount(parameterApiModel, reqConfig?) {
        return this.fiBackend.fetchDataPost<number>(
            'CheckList/CheckList/GetCheckListsCount',
            parameterApiModel,
            reqConfig,
        );
    }

    getCheckLists(parameterApiModel, reqConfig?) {
        return this.fiBackend.fetchDataPost<ChecklistModel[]>(
            'CheckList/CheckList/GetCheckLists',
            parameterApiModel,
            reqConfig,
        );
    }

    deleteCheckList(id) {
        return this.fiBackend.fetchDataPost<void>('CheckList/CheckList/DeleteCheckList/' + id);
    }

    saveChecklistAsTemplate(id) {
        return this.fiBackend.fetchDataPost(`CheckList/CheckList/SaveAsTemplate/${id}`);
    }

    getCheckPointAnswersCount(checkListId: number) {
        return this.fiLoadingService.trackPromise(
            this.fiBackend.fetchData<number>(`CheckList/CheckList/GetCheckPointAnswersCount/${checkListId}`),
        );
    }

    deleteChecklistResponses(id) {
        return this.fiBackend.fetchDataPost(`CheckList/CheckList/DeleteAnswers/${id}`);
    }

    GetLightWeightObjectsForLocalSearch(parameterApiModel, reqConfig?) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'CheckList/CheckList/GetLightWeightObjectsForLocalSearch',
            parameterApiModel,
            reqConfig,
        );
    }

    getCheckListTemplatesListData(parameter: IItemsListParameterBase) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<any>>(
            'CheckList/CheckList/GetCheckListTemplatesListData',
            parameter,
        );
    }

    GetCheckListTemplatesCount(parameterApiModel, reqConfig?) {
        return this.fiBackend.fetchDataPost<number>(
            'CheckList/CheckList/GetCheckListTemplatesCount',
            parameterApiModel,
            reqConfig,
        );
    }

    GetCheckListTemplates(parameterApiModel, reqConfig?) {
        return this.fiBackend.fetchDataPost<any[]>(
            'CheckList/CheckList/GetCheckListTemplates',
            parameterApiModel,
            reqConfig,
        );
    }

    GetLightWeightCheckListTemplates(reqConfig?) {
        return this.fiBackend.fetchData<ILightWeightObject[]>(
            'CheckList/CheckList/GetLightWeightCheckListTemplates',
            reqConfig,
        );
    }

    getCheckListTemplate(id) {
        return this.fiBackend.fetchObjectProj('CheckList/CheckList/GetCheckListTemplate/' + id, (data) =>
            this.getChecklistInstance(data),
        );
    }

    GetTemplatesLightWeightObjectsForLocalSearch(parameterApiModel, reqConfig?) {
        return this.fiBackend.fetchDataPost<ILightWeightObjectCollection>(
            'CheckList/CheckList/GetTemplatesLightWeightObjectsForLocalSearch',
            parameterApiModel,
            reqConfig,
        );
    }

    saveCheckListViewer(checklistViewer, reqConfig?) {
        return this.saveChecklistViewer(checklistViewer, reqConfig).then((result) => {
            return result;
        });
    }

    SetCheckPointNotApplicable(checkpointId, isSetNotApplicable) {
        return this.fiBackend.fetchDataPost<void>(
            'CheckList/CheckList/SetCheckPointNotApplicable/' + checkpointId + '/' + isSetNotApplicable,
        );
    }

    DeleteCheckListTemplates(ids, reqConfig?) {
        return this.fiBackend.fetchDataPost('CheckList/CheckList/DeleteCheckListTemplates', ids, reqConfig);
    }

    public isEditAllowedByModuleRights() {
        return (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.ChecklistLocal,
                AccessRightType.Share,
            ) ||
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.ChecklistCentral,
                AccessRightType.Share,
            )
        );
    }

    getUserAllowedActions(): {
        setNotApplicable: IItemsListAction<ChecklistModel>;
        turnOffReminder: IItemsListAction<ChecklistModel>;
        viewReports: IItemsListAction<ChecklistModel>;
        saveAsTemplate: IItemsListAction<ChecklistModel>;
        editChecklist: IItemsListAction<ChecklistModel>;
        deleteResponses: IItemsListAction<ChecklistModel>;
        deleteChecklist: IItemsListAction<IFiChecklistModelBase>;
    } {
        const ChecklistLocal = ApplicationSettingsType.ChecklistLocal,
            isShareRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
                ChecklistLocal,
                AccessRightType.Share,
            ),
            isReportReadRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.CheckListReports,
                AccessRightType.Read,
            ),
            isShareRightsCheck = () => {
                return isShareRights;
            },
            isAdminRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
                ChecklistLocal,
                AccessRightType.Admin,
            ),
            isAdminRightsCheck = () => {
                return isAdminRights;
            };
        return {
            setNotApplicable: {
                title: this.appTextService.get('checklist.not_applicable'),
                condition: isShareRightsCheck,
                onClick: (checklist) => {
                    return this.fiLoadingService
                        .trackPromise(this.setChecklistNotApplicable(checklist.Id, !checklist.IsSetNotApplicable))
                        .then((newIsSetNotApplicable) => {
                            checklist.IsSetNotApplicable = newIsSetNotApplicable;
                        });
                },
                isChecked: (checklist) => {
                    return checklist.IsSetNotApplicable;
                },
            },
            turnOffReminder: {
                title: this.appTextService.get('checklist.turn_off_reminder'),
                //condition: isShareRightsCheck, //  functionality should be allowed for everyone
                onClick: (checklist) => {
                    this.turnOffChecklistReminder(checklist.Id, !checklist.IsTurnOffReminder).then(
                        (newIsTurnOffReminder) => {
                            checklist.IsTurnOffReminder = newIsTurnOffReminder;
                        },
                    );
                },
                isDisabled: (checklist) => {
                    return checklist.IsSetNotApplicable;
                },
                isChecked: (checklist) => {
                    return !checklist.IsTurnOffReminder;
                },
            },
            viewReports: {
                title: this.appTextService.get('checklist.view_reports'),
                condition: (checklist) => {
                    return checklist.HasReportsAccess && isReportReadRights;
                },
                onClick: (data) => {
                    this.stateService.go(FiStateNames.Checklist.Report.View, { Id: data.Id });
                },
            },
            saveAsTemplate: {
                title: this.appTextService.get('checklist.save_as_template'),
                condition: isAdminRightsCheck,
                onClick: (data) => {
                    this.fiLoadingService.trackPromise(this.saveChecklistAsTemplate(data.Id)).then(() => {
                        this.fiDialogsService.notify({
                            messageText: this.appTextService.get('checklist.save_as_template_success'),
                        });
                    });
                },
            },
            editChecklist: {
                title: this.appTextService.get('checklist.edit_checklist'),
                condition: (data) => {
                    return (
                        this.isEditAllowedByModuleRights() && data.HasEditAccess && !this.detectDevices.isMobilePhone()
                    );
                },
                onClick: (data) => {
                    this.stateService.go(FiStateNames.Checklist.Edit, { Id: data.Id });
                },
            },
            deleteResponses: {
                title: this.appTextService.get('checklist.delete_responses'),
                condition: (data) => {
                    return data.HasEditAccess && isShareRights;
                },
                isDisabled: (data) => {
                    return !data.IsWithResponses /* || data.IsPublished*/;
                },
                onClick: (checklist) => {
                    const promise = new Promise<void>((resolve, reject) => {
                        this.getCheckPointAnswersCount(checklist.Id).then((answersCount) => {
                            this.processUserDeleteResponses(checklist.Id, checklist.Name, answersCount).then(() => {
                                checklist.IsWithResponses = false;
                                resolve();
                            }, reject);
                        }, reject);
                    });
                    return promise;
                },
            },
            deleteChecklist: {
                title: this.appTextService.get('checklist.delete_checklist'),
                condition: (data) => {
                    return this.isEditAllowedByModuleRights() && data.HasEditAccess;
                },
                isDisabled: (data) => {
                    return false;
                    //return data.PublicationSetting.IsShared;
                },
                onClick: (data) => {
                    const promise = new Promise<void>((resolve, reject) => {
                        const dialogContentData: IDialogConfirmDeleteData = {
                            step1: {
                                message: 'checklist.confirm_delete_checklist_text',
                                buttonText: this.appTextService.get('common.confirm'),
                                confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                            },
                            step2: data.IsWithResponses
                                ? {
                                      message: 'checklist.confirm_delete_checklist_text_2',
                                      buttonText: this.appTextService.get('checklist.delete_checklist').toUpperCase(),
                                  }
                                : undefined,
                        };

                        this.fiDialogsService
                            .custom({
                                headerText: this.appTextService.get('common.warning'),
                                contentComponentRef: FiDialogConfirmDeleteComponent,
                                contentComponentData: dialogContentData,
                                preventUppercaseButtonLabel: true,
                                config: {
                                    maxWidth: '400px',
                                },
                            })
                            .onYesButtonClick(() => {
                                this.fiLoadingService.trackPromise(this.deleteCheckList(data.Id)).then((response) => {
                                    this.fiDialogsService.notify({
                                        messageText: this.appTextService.get('checklist.checklist_deleted'),
                                    });
                                    resolve(response /*, data*/);
                                }, reject);
                            })
                            .onNoButtonClick(reject);
                    });

                    return promise;
                },
            },
        };
    }

    processUserDeleteResponses(checklistId: number, checklistName: string, answersCount: number) {
        const promise = new Promise<void>((resolve, reject) => {
            const dialogContentData: IDialogConfirmDeleteData = {
                step1: {
                    message: this.appTextService.get('checklist.confirm_delete_responses_text', {
                        '{0}': fiTextToHtml(checklistName),
                        '{1}': answersCount,
                    }),
                    buttonText: this.appTextService.get('common.confirm'),
                    confirmTextToInput: this.appTextService.get('common.delete').toUpperCase(),
                },
                step2: {
                    message: 'checklist.confirm_delete_responses_text_2',
                    buttonText: this.appTextService.get('checklist.delete_responses').toUpperCase(),
                },
            };

            this.fiDialogsService
                .custom({
                    headerText: this.appTextService.get('checklist.confirm_delete_responses_header'),
                    contentComponentRef: FiDialogConfirmDeleteComponent,
                    contentComponentData: dialogContentData,
                    preventUppercaseButtonLabel: true,
                    config: {
                        maxWidth: '400px',
                    },
                })
                .onYesButtonClick(() => {
                    this.fiLoadingService.trackPromise(promise);

                    this.deleteChecklistResponses(checklistId)
                        .catch(reject)
                        .then(resolve)
                        .then(() => {
                            this.snackBar.showSnackbar({
                                textMessage: this.appTextService.get('common.responses_deleted'),
                                buttonText: this.appTextService.get('common.dismiss'),
                            });
                        });
                })
                .onNoButtonClick(reject);
        });

        return promise;
    }

    getTimeSettingsName(id) {
        if (this.timeSettingsMap.hasOwnProperty(id)) return this.appTextService.get(this.timeSettingsMap[id].textId);

        throw 'FI: checklistService.getTimeSettingsName: Unknown time settings: ' + id;
    }

    getCheckListViewerByAnswerId(checkListId, checkPointAnswerId) {
        return this.fiBackend.fetchData(
            `CheckList/CheckList/GetCheckListViewerByAnswerId/${checkListId}/${checkPointAnswerId}`,
        );
    }

    getEndDateText(checklist: ChecklistModel, endDate) {
        return checklist.PublicationSetting.PublicationType === PublicationType.Continous
            ? this.appTextService.get('common.unlimited')
            : this.fiFormatDateService.formatDate(endDate, 'date');
    }

    private _checkPointAnswerNotifications$ = new BehaviorSubject<ISystemNotificationsCheckpointAnswer[]>([]);
    get checkPointAnswerNotifications$() {
        return this._checkPointAnswerNotifications$.asObservable();
    }
    get checkPointAnswerNotifications() {
        return this._checkPointAnswerNotifications$.value;
    }
    initCheckPointAnswersForSystemNotifications() {
        this.fiSignalRHelperService.initialCheckPointAnswerNotifications.subscribe((data) => {
            this._checkPointAnswerNotifications$.next(data);
        });

        /*return this.fiBackend.fetchData<ISystemNotificationsCheckpointAnswer[]>("Core/CheckList/GetCheckPointAnswerNotifications").then(data => {
            this.checkPointAnswerNotifications$.next(data);
        });*/

        /*this.fiSignalRService.onNewsFeedUpdate((data) => {
            if (data.ObjectType === ObjectTypes.CheckPointAnswerNotification) {
                this.getCheckPointAnswerNotificationsCount().then(() => {
                });
                this.notificationsChange.emit(data);
            }
        });*/ // todo can lead to performance issues

        //return this.getCheckPointAnswerNotificationsCount();
    }

    onDeleteCheckPointAnswerNotifications(checkPointAnswerNotificationIds: number[]) {
        const newCheckPointAnswerNotifications = this.checkPointAnswerNotifications.filter(
            (item) => checkPointAnswerNotificationIds.indexOf(item.Id) === -1,
        );
        this._checkPointAnswerNotifications$.next(newCheckPointAnswerNotifications);
    }
}
